import { ChangeEvent, FormEvent, useCallback, useState } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { Field, Icon, TextInput } from "@zapier/design-system";
import { Button } from "@zapier/design-system-beta";
import {
  zslExecutionFailedErrorMsgAtom,
  zslAtom,
  emitAiPredictionUserFeedbackEventSetter,
  zslFeedbackPredictionIdAtom,
} from "@zapier/canvas-state";
import { IMPLICIT_BAD } from "@zapier/canvas-constants";

import { useSyncZslStreamToOutline } from "../../hooks/useSyncZslStreamToOutline";
import { aiPromptAtom } from "../../atoms/aiPromptAtom";
import { tuningPromptsAtom } from "../../atoms/tuningPromptsAtom";
import { renderZslToCanvasSetter } from "../../atoms/renderZslToCanvasSetter";
import { isStreamingZslAtom } from "../../atoms/streamingStatusAtoms";
import { AiSparkle } from "../AiSparkle";
import styles from "./ModifyPromptSidebar.module.css";

export function ModifyPromptSidebar() {
  const prompt = useAtomValue(aiPromptAtom);
  const [additionalChanges, setAdditionalChanges] = useState("");
  const submissions = useAtomValue(tuningPromptsAtom);
  const [error, setError] = useState("");
  const submitZslToCanvas = useSetAtom(renderZslToCanvasSetter);
  const isLoading = useAtomValue(isStreamingZslAtom);

  const { handleSubmit } = useSyncZslStreamToOutline({
    api: "/api/zsl/v2/combined_stream",
    async onComplete() {
      await submitZslToCanvas();
    },
    onError(error: string) {
      setError(error);
    },
  });

  const handleTextAreaChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      setAdditionalChanges(e.target.value);
    },
    [setAdditionalChanges],
  );

  const handleFormSubmit = useAtomCallback(
    useCallback(
      async (get, set, e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const trimmedChanges = additionalChanges.trim();
        if (trimmedChanges) {
          const prediction_id = get(zslFeedbackPredictionIdAtom);
          const prompt = get(aiPromptAtom);
          const zsl = get(zslAtom);
          const prevTuningPrompts = get(tuningPromptsAtom);
          const tuningPrompts = [...prevTuningPrompts, trimmedChanges];

          // Add new submission to the list
          set(tuningPromptsAtom, (prev) => [...prev, trimmedChanges]);

          // Clear the input field
          setAdditionalChanges("");

          set(emitAiPredictionUserFeedbackEventSetter, {
            prediction_id,
            correlation_id: null,
            user_rating: IMPLICIT_BAD,
            user_feedback: JSON.stringify({
              prevZsl: zsl,
              tuningPrompts,
            }),
          });

          set(zslExecutionFailedErrorMsgAtom, null); // Clear out old error alert
          await handleSubmit(prompt, zsl, tuningPrompts);

          /* TODO: Disable / hide x button while updating system */
        }
      },
      [additionalChanges, handleSubmit],
    ),
  );

  // Original implementation for when the feature flag is off
  return (
    <form className={styles.form} onSubmit={handleFormSubmit}>
      <p className={styles.description}>
        Add to the original prompt and let Copilot fine-tune the generated
        system.
      </p>
      <Field
        label="Original prompt"
        renderInput={(inputProps) => (
          <TextInput
            {...inputProps}
            isMultiline={true}
            isDisabled={true}
            disabledText=""
            name="Original prompt"
            rows={3}
            placeholder={prompt}
          />
        )}
      />
      {submissions.length > 0 && (
        <div>
          {submissions.map((submission, index) => (
            <div key={index} className={styles.submission}>
              <div className={styles.leftColumn}>
                <AiSparkle height={20} width={20} />
              </div>
              <p>{submission}</p>
            </div>
          ))}
        </div>
      )}
      <Field
        isRequired={true}
        label="Additional changes"
        error={
          error ? `We couldn't process your changes. Try again.` : undefined
        }
        renderInput={(inputProps) => (
          <TextInput
            {...inputProps}
            isMultiline={true}
            rows={3}
            value={additionalChanges}
            onChange={handleTextAreaChange}
            name="message"
          />
        )}
      />
      <Button
        type="submit"
        isDisabled={!additionalChanges}
        isLoading={isLoading}
      >
        <Icon name="miscAI" size={30} />
        Update system
      </Button>
    </form>
  );
}
